import React from 'react'

export interface OtpInputProps {
  length?: number
  value: string
  onChange: (value: string) => void
  onComplete?: (value: string) => void
  error?: boolean
  disabled?: boolean
  autoFocus?: boolean
}

export const OtpInput: React.FC<OtpInputProps> = ({
  length = 6,
  value,
  onChange,
  onComplete,
  error = false,
  disabled = false,
  autoFocus = true,
}) => {
  const inputRefs = React.useRef<Array<HTMLInputElement | null>>([])

  const digits = React.useMemo(() => {
    const arr = value.split('').slice(0, length)
    while (arr.length < length) arr.push('')
    return arr
  }, [value, length])

  const setDigit = (index: number, digit: string) => {
    const next = [...digits]
    next[index] = digit
    const nextValue = next.join('')
    onChange(nextValue)
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus()
    }
    if (next.every((d) => d !== '')) {
      onComplete?.(nextValue)
    }
  }

  const handleChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1)
    setDigit(index, digit)
  }

  const handleKeyDown = (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
      setDigit(index - 1, '')
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!pasted) return
    onChange(pasted)
    const lastIndex = Math.max(Math.min(pasted.length, length) - 1, 0)
    inputRefs.current[lastIndex]?.focus()
    if (pasted.length === length) {
      onComplete?.(pasted)
    }
  }

  return (
    <div className="flex items-center justify-center gap-2.5" role="group" aria-label="Verification code">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={digit}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste}
          aria-label={`Digit ${index + 1}`}
          className={
            'h-14 w-11 rounded-md border text-center bg-white text-[#0f1011] ' +
            'dark:bg-white/[0.02] dark:text-[#f7f8f8] focus:outline-none transition-colors duration-150 ' +
            (error
              ? 'border-red-500/60 focus:border-red-500/70'
              : 'border-black/[0.08] dark:border-white/[0.08] focus:border-[#5e6ad2]/70 dark:focus:border-[#7170ff]/75')
          }
          style={{ fontSize: '20px', fontWeight: 510 }}
        />
      ))}
    </div>
  )
}
